import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useSWRConfig } from 'swr'
import {
  SOCIAL_MAX_BRANDS,
  SOCIAL_MAX_KEYWORDS,
  SOCIAL_MAX_LANGUAGES,
  SOCIAL_MAX_TOPICS,
  SOCIAL_SOURCE_CHECK_MAX_CREDITS,
  SOCIAL_SUGGESTED_KEYWORDS_MAX,
  SocialBrandAccount,
  SocialBrandInput,
  SocialCommunityMode,
  SocialKeywordWarning,
  SocialPlatform,
  SocialRefusedBrand,
  SocialSubjectKind,
  SocialRefusedSource,
  SocialSourceCheck,
  SocialSourceCheckKind,
  SocialSourceCheckResponse,
  SocialTopicInput,
  SocialUniverseItem,
  SocialUniverseSuggestion,
} from '@digital-twins/shared-types'

import { useSocialListeningApi } from './SocialListeningApiContext'
import { socialLimitErrorBody } from './socialLimitErrors'
import type { SocialMarketValue } from './socialMarket'
import { refreshSocialPostsUsage } from './useSocialPostsUsage'
import { SOCIAL_UNIVERSE_SWR_KEY, useSocialUniverseRuns } from './useSocialUniverse'
import {
  applyUniverseSuggestion,
  changesTags,
  emptyUniverseDrafts,
  flushUniverseDrafts,
  initialUniverseForm,
  firstBlockedUniverseStep,
  missingUniverseFields,
  newSuggestedNames,
  removedPlatforms,
  SOCIAL_UNIVERSE_PLATFORM_ORDER,
  SocialSourceCheckState,
  socialHashtagValue,
  toUniverseInput,
  typedBrandAccount,
  withChipValues,
  withCommunityNames,
  withUniverseMarket,
  withoutStaleSuggestions,
  suggestedOf,
  toUniverseUpdate,
  UniverseDrafts,
  UniverseFormState,
  UniverseStep,
  UniverseSuggested,
} from './socialUniverseForm'

/** Why the last Suggest failed, as the dialog words it: paused, spend limit reached, or anything else. */
export type SocialUniverseSuggestFailureKind = 'paused' | 'spend_cap' | 'generic'

/** The typed `reason` of a Suggest 503 (`error.response.data.reason`); anything else is generic. */
export function socialUniverseSuggestFailureKind(error: unknown): SocialUniverseSuggestFailureKind {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data
  const reason =
    data && typeof data === 'object' ? (data as { reason?: unknown }).reason : undefined
  if (reason === 'paused' || reason === 'spend_cap') {
    return reason
  }
  return 'generic'
}

/** One source the form checks: a proposed hashtag or community (by its id), or a brand's account. */
interface FormCheckTarget {
  platform: SocialPlatform
  kind: SocialSourceCheckKind
  value: string
  /** The Suggest id of a proposed hashtag or community; null for an account. */
  id: string | null
  /** The brand an account is for; null for hashtags and communities. */
  brandName: string | null
}

function sourceKey(
  kind: SocialSourceCheckKind,
  platform: SocialPlatform,
  value: string,
  brandName: string | null,
): string {
  return JSON.stringify([kind, platform, value, brandName])
}

/**
 * What Suggest proposed that the form now holds, to check: hashtags and
 * communities it added (by id), then the accounts of the brands it offers.
 */
function suggestedCheckTargets(
  applied: UniverseFormState,
  suggestion: SocialUniverseSuggestion,
  brands: Array<SocialBrandInput>,
): Array<FormCheckTarget> {
  const targets: Array<FormCheckTarget> = []
  for (const platform of SOCIAL_UNIVERSE_PLATFORM_ORDER) {
    for (const hashtag of suggestion.hashtags?.[platform] ?? []) {
      if (
        hashtag.check.status === 'unchecked' &&
        applied.hashtags[platform].some((entry) => entry.id === hashtag.id)
      ) {
        targets.push({ platform, kind: 'hashtag', value: hashtag.tag, id: hashtag.id, brandName: null })
      }
    }
    for (const community of suggestion.communities?.[platform] ?? []) {
      if (
        community.check.status === 'unchecked' &&
        applied.communities[platform].some((entry) => entry.id === community.id)
      ) {
        targets.push({ platform, kind: 'community', value: community.name, id: community.id, brandName: null })
      }
    }
  }
  for (const brand of brands) {
    for (const platform of SOCIAL_UNIVERSE_PLATFORM_ORDER) {
      const account = brand.accounts?.[platform]
      if (account && account.check.status === 'unchecked') {
        targets.push({ platform, kind: 'account', value: account.handle, id: null, brandName: brand.name })
      }
    }
  }
  return targets
}

/** One check call per channel, at most SOCIAL_SOURCE_CHECK_MAX_CREDITS targets each, so each channel's labels fill as its call answers. */
function checkBatches(targets: Array<FormCheckTarget>): Array<Array<FormCheckTarget>> {
  return SOCIAL_UNIVERSE_PLATFORM_ORDER.flatMap((platform) => {
    const own = targets.filter((target) => target.platform === platform)
    const batches: Array<Array<FormCheckTarget>> = []
    for (let start = 0; start < own.length; start += SOCIAL_SOURCE_CHECK_MAX_CREDITS) {
      batches.push(own.slice(start, start + SOCIAL_SOURCE_CHECK_MAX_CREDITS))
    }
    return batches
  })
}

export interface UseSocialUniverseWizardProps {
  /** Null creates a Topic; a Topic edits it. */
  universe: SocialUniverseItem | null
  onOpenChange: (open: boolean) => void
  onSaved: (universe: SocialUniverseItem) => void
}

/** State and actions of the New topic / Edit topic dialog. */
export function useSocialUniverseWizard(props: UseSocialUniverseWizardProps) {
  const { universe, onOpenChange, onSaved } = props

  const { t } = useTranslation('app')
  const api = useSocialListeningApi()
  const { mutate: mutateCache } = useSWRConfig()
  const [form, setForm] = useState<UniverseFormState>(() =>
    initialUniverseForm(universe),
  )
  const [drafts, setDrafts] = useState<UniverseDrafts>(emptyUniverseDrafts)
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isSuggesting, setIsSuggesting] = useState(false)
  const [suggestFailure, setSuggestFailure] = useState<SocialUniverseSuggestFailureKind | null>(null)
  /** Keywords Suggest offered that the Topic does not have yet; added one by one. */
  const [suggestedKeywords, setSuggestedKeywords] = useState<Array<string>>([])
  /** Brands and themes Suggest offered past their limits; added one by one, never replacing the user's. */
  const [suggestedBrands, setSuggestedBrands] = useState<Array<SocialBrandInput>>([])
  const [suggestedThemes, setSuggestedThemes] = useState<Array<SocialTopicInput>>([])
  const [keywordWarnings, setKeywordWarnings] = useState<Array<SocialKeywordWarning>>([])
  /** What the last Suggest did not keep, each with its typed reason. */
  const [refusedBrands, setRefusedBrands] = useState<Array<SocialRefusedBrand>>([])
  const [refusedSources, setRefusedSources] = useState<Array<SocialRefusedSource>>([])
  /** Per source key: its check is running, or why it gave no answer. */
  const [checkStates, setCheckStates] = useState<Record<string, SocialSourceCheckState>>({})
  const [pendingRemoval, setPendingRemoval] = useState<SocialPlatform | null>(
    null,
  )
  const [error, setError] = useState<string | null>(null)
  /** True when the last save failed with `402 social_out_of_credits` (create without funds, E14). */
  const [isOutOfCredits, setIsOutOfCredits] = useState(false)
  const alive = useRef(true)
  const saving = useRef(false)
  const suggestRequest = useRef(0)
  /** True once the client picked the subject kind, or the Topic was saved with one: Suggest leaves it. */
  const subjectKindPicked = useRef(universe !== null)
  /** What the last Suggest added, and the languages and market it ran for; null before one. */
  const lastSuggested = useRef<UniverseSuggested | null>(null)
  const latestForm = useRef(form)
  latestForm.current = form

  const { runs } = useSocialUniverseRuns(universe?.token ?? null)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      suggestRequest.current += 1
    }
  }, [])


  function updateField<K extends keyof UniverseFormState>(
    key: K,
    value: UniverseFormState[K],
  ) {
    setForm((previous) => ({ ...previous, [key]: value }))
    setError(null)
  }

  /** A market pick: the languages follow it while the user has not picked one (`withUniverseMarket`). */
  function updateMarket(picked: SocialMarketValue) {
    setForm((previous) => ({ ...previous, ...withUniverseMarket(previous, picked) }))
    setError(null)
  }

  function updateDraft(key: 'keywords' | 'brands', value: string) {
    setDrafts((previous) => ({ ...previous, [key]: value }))
    setError(null)
  }

  function updateHandleDraft(platform: SocialPlatform, value: string) {
    setDrafts((previous) => ({
      ...previous,
      handles: { ...previous.handles, [platform]: value },
    }))
    setError(null)
  }

  function updateHandles(platform: SocialPlatform, values: Array<string>) {
    setForm((previous) => ({
      ...previous,
      handles: { ...previous.handles, [platform]: values },
    }))
    setError(null)
  }

  function updateSearchDraft(platform: SocialPlatform, value: string) {
    setDrafts((previous) => ({
      ...previous,
      searches: { ...previous.searches, [platform]: value },
    }))
    setError(null)
  }

  function updateSearches(platform: SocialPlatform, values: Array<string>) {
    setForm((previous) => ({
      ...previous,
      searches: {
        ...previous.searches,
        [platform]: withChipValues(
          previous.searches[platform],
          values.slice(0, SOCIAL_MAX_KEYWORDS - previous.hashtags[platform].length),
          (search) => search.query,
          (query) => ({ query }),
        ),
      },
    }))
    setError(null)
  }

  function updateHashtagDraft(platform: SocialPlatform, value: string) {
    setDrafts((previous) => ({
      ...previous,
      hashtags: { ...previous.hashtags, [platform]: value },
    }))
    setError(null)
  }

  function updateHashtags(platform: SocialPlatform, values: Array<string>) {
    setForm((previous) => ({
      ...previous,
      hashtags: {
        ...previous.hashtags,
        [platform]: withChipValues(
          previous.hashtags[platform],
          [...new Set(values.map(socialHashtagValue).filter(Boolean))].slice(
            0,
            SOCIAL_MAX_KEYWORDS - previous.searches[platform].length,
          ),
          (hashtag) => hashtag.tag,
          (tag) => ({ tag }),
        ),
      },
    }))
    setError(null)
  }

  function updateCommunityDraft(mode: SocialCommunityMode, platform: SocialPlatform, value: string) {
    setDrafts((previous) => ({
      ...previous,
      communities: {
        ...previous.communities,
        [mode]: { ...previous.communities[mode], [platform]: value },
      },
    }))
    setError(null)
  }

  function updateCommunities(mode: SocialCommunityMode, platform: SocialPlatform, names: Array<string>) {
    setForm((previous) => ({
      ...previous,
      communities: {
        ...previous.communities,
        [platform]: withCommunityNames(previous.communities[platform], mode, names),
      },
    }))
    setError(null)
  }

  /** Sets, or with null removes, one brand's account on one platform. */
  function setBrandAccount(
    brandIndex: number,
    platform: SocialPlatform,
    account: SocialBrandAccount | null,
  ) {
    setForm((previous) => ({
      ...previous,
      brands: previous.brands.map((brand, index) => {
        if (index !== brandIndex) {
          return brand
        }
        const accounts = { ...(brand.accounts ?? {}) }
        if (account) {
          accounts[platform] = account
        } else {
          delete accounts[platform]
        }
        return { ...brand, accounts }
      }),
    }))
    setError(null)
  }

  function setCheckState(key: string, state: SocialSourceCheckState | null) {
    setCheckStates((current) => {
      const next = { ...current }
      if (state) {
        next[key] = state
      } else {
        delete next[key]
      }
      return next
    })
  }

  /**
   * Sets the check (and an account's provider id) on every copy of the source
   * the form or the brand chips hold. An account the check verified (posts in
   * the window) comes confirmed, and the client unticks it to leave it out
   * (D20); any other answer leaves its confirmation as it was.
   */
  function setSourceCheck(target: FormCheckTarget, check: SocialSourceCheck, providerId: string | null) {
    const withAccountCheck = (brand: SocialBrandInput): SocialBrandInput => {
      const current = brand.accounts?.[target.platform]
      return brand.name === target.brandName && current?.handle === target.value
        ? {
            ...brand,
            accounts: {
              ...brand.accounts,
              [target.platform]: {
                ...current,
                check,
                providerId,
                confirmed: check.status === 'verified' ? true : current.confirmed,
              },
            },
          }
        : brand
    }
    if (target.kind === 'account') {
      setSuggestedBrands((current) => current.map(withAccountCheck))
    }
    setForm((previous) => {
      if (target.kind === 'account') {
        return { ...previous, brands: previous.brands.map(withAccountCheck) }
      }
      if (target.kind === 'hashtag') {
        return {
          ...previous,
          hashtags: {
            ...previous.hashtags,
            [target.platform]: previous.hashtags[target.platform].map((entry) =>
              entry.id === target.id ? { ...entry, check } : entry,
            ),
          },
        }
      }
      return {
        ...previous,
        communities: {
          ...previous.communities,
          [target.platform]: previous.communities[target.platform].map((entry) =>
            entry.id === target.id ? { ...entry, check } : entry,
          ),
        },
      }
    })
  }

  /**
   * A proposal whose check found no such source is not kept: it leaves the
   * form and the brand chips and is stated, as Suggest's refusals were. A
   * source the client kept explicitly (an account confirmed, a tag typed
   * again) stays, with its "Not found" label.
   */
  function refuseNotFound(target: FormCheckTarget, check: SocialSourceCheck) {
    const isKept =
      target.kind === 'account' &&
      latestForm.current.brands.some(
        (brand) =>
          brand.name === target.brandName &&
          brand.accounts?.[target.platform]?.handle === target.value &&
          brand.accounts[target.platform]?.confirmed,
      )
    if (isKept) {
      setSourceCheck(target, check, null)
      return
    }
    const withoutAccount = (brand: SocialBrandInput): SocialBrandInput => {
      if (brand.name !== target.brandName || brand.accounts?.[target.platform]?.handle !== target.value) {
        return brand
      }
      const accounts = { ...brand.accounts }
      delete accounts[target.platform]
      return { ...brand, accounts }
    }
    if (target.kind === 'account') {
      setSuggestedBrands((current) => current.map(withoutAccount))
    }
    setForm((previous) =>
      target.kind === 'account'
        ? { ...previous, brands: previous.brands.map(withoutAccount) }
        : target.kind === 'hashtag'
          ? {
              ...previous,
              hashtags: {
                ...previous.hashtags,
                [target.platform]: previous.hashtags[target.platform].filter((entry) => entry.id !== target.id),
              },
            }
          : {
              ...previous,
              communities: {
                ...previous.communities,
                [target.platform]: previous.communities[target.platform].filter((entry) => entry.id !== target.id),
              },
            },
    )
    setRefusedSources((current) => [
      ...current,
      {
        platform: target.platform,
        kind: target.kind,
        value: target.value,
        brandName: target.brandName,
        refusal: 'not_found',
      },
    ])
  }

  /**
   * Checks `targets` through the check endpoint, one call per channel (each
   * under the per-click cap), all at once; each source's label fills as its
   * call answers. `isCurrent` drops answers a newer Suggest or a closed
   * dialog made stale.
   */
  async function checkSources(targets: Array<FormCheckTarget>, isCurrent: () => boolean) {
    if (targets.length === 0) {
      return
    }
    const keyOf = (target: FormCheckTarget) =>
      sourceKey(target.kind, target.platform, target.value, target.brandName)
    setCheckStates((current) => ({
      ...current,
      ...Object.fromEntries(targets.map((target) => [keyOf(target), 'checking' as const])),
    }))
    const { depthDays, marketCode } = latestForm.current
    await Promise.all(
      checkBatches(targets).map(async (batch) => {
        let response: SocialSourceCheckResponse
        try {
          response = await api.checkSocialSources({
            targets: batch.map(({ platform, kind, value }) => ({ platform, kind, value })),
            depthDays,
            marketCode,
          })
        } catch {
          if (isCurrent()) {
            batch.forEach((target) => setCheckState(keyOf(target), 'request_failed'))
          }
          return
        }
        if (!isCurrent()) {
          return
        }
        batch.forEach((target, index) => {
          const result = response.results[index]
          if (!result || result.check.status === 'unchecked') {
            setCheckState(keyOf(target), result?.failure ?? 'request_failed')
            return
          }
          setCheckState(keyOf(target), null)
          if (result.check.status === 'not_found') {
            refuseNotFound(target, result.check)
          } else {
            setSourceCheck(target, result.check, result.providerId)
          }
        })
      }),
    )
  }

  /** An account the client typed: confirmed at once, then checked by its posts feed (one check call). */
  async function addTypedBrandAccount(brandIndex: number, platform: SocialPlatform, handle: string) {
    const account = typedBrandAccount(handle)
    if (!account.handle) {
      return
    }
    const brandName = latestForm.current.brands[brandIndex]?.name ?? ''
    setBrandAccount(brandIndex, platform, account)
    await checkSources(
      [{ platform, kind: 'account', value: account.handle, id: null, brandName }],
      () => alive.current,
    )
  }

  /**
   * Runs only on the Suggest button: never on its own. It reads the goal, the
   * market and the languages (D21b); a subject already typed goes as a hint.
   * It proposes for every channel: the picker comes after it, on step 2, and
   * only the picked channels are saved.
   */
  async function suggest() {
    const next = flushDrafts()
    if (!next.goal.trim()) {
      return
    }
    const id = ++suggestRequest.current
    setIsSuggesting(true)
    setSuggestFailure(null)
    try {
      const suggestion = await api.suggestUniverse({
        ...(next.name.trim() ? { name: next.name.trim() } : {}),
        // The server takes SOCIAL_MAX_KEYWORDS; the Topic step asks to trim the rest.
        keywords: next.keywords.slice(0, SOCIAL_MAX_KEYWORDS),
        channels: [...SOCIAL_UNIVERSE_PLATFORM_ORDER],
        ...(next.market.trim() ? { market: next.market.trim() } : {}),
        ...(next.marketCode ? { marketCode: next.marketCode } : {}),
        ...(next.languages.length ? { languages: next.languages } : {}),
        goal: next.goal.trim(),
      })
      if (!alive.current || id !== suggestRequest.current) {
        return
      }
      // Each Suggest replaces its own earlier proposals, never the user's.
      const base = withoutStaleSuggestions(latestForm.current, lastSuggested.current)
      const suggestedApplied = applyUniverseSuggestion(base, suggestion)
      // Suggest fills the subject kind (D24), never over the client's own pick.
      const applied =
        suggestion.subjectKind && !subjectKindPicked.current
          ? { ...suggestedApplied, subjectKind: suggestion.subjectKind }
          : suggestedApplied
      const suggested = suggestedOf(base, applied)
      lastSuggested.current = suggested
      latestForm.current = applied
      setForm(applied)
      // Absent on servers before keyword suggestions.
      setSuggestedKeywords(
        [...new Set(suggestion.keywords ?? [])]
          .filter((keyword) => keyword.trim() && !applied.keywords.includes(keyword))
          .slice(0, SOCIAL_SUGGESTED_KEYWORDS_MAX),
      )
      const brandNames = newSuggestedNames(
        applied.brands.map((brand) => brand.name),
        suggestion.brands.map((brand) => brand.name),
      )
      // Each brand keeps what Suggest wrote for it: searches, hashtags, role, aliases
      // and its checked accounts, which the client confirms or removes.
      const offeredBrands = brandNames.flatMap((name) => {
        const brand = suggestion.brands.find((entry) => entry.name.trim() === name)
        return brand ? [{ ...brand, name, description: brand.description ?? '' }] : []
      })
      setSuggestedBrands(offeredBrands)
      setRefusedBrands(suggestion.refusedBrands ?? [])
      setRefusedSources([])
      const themeNames = newSuggestedNames(
        applied.topics.map((topic) => topic.name),
        suggestion.topics.map((topic) => topic.name),
      )
      setSuggestedThemes(
        themeNames.map((name) => ({
          name,
          description:
            suggestion.topics.find((topic) => topic.name.trim() === name)?.description ?? '',
        })),
      )
      setKeywordWarnings(suggestion.keywordWarnings ?? [])
      // Suggest answers unchecked: its proposals are checked now, their labels filling as each channel answers.
      void checkSources(
        suggestedCheckTargets(applied, suggestion, [
          ...applied.brands.filter((brand) => suggested.brands.includes(brand.name)),
          ...offeredBrands,
        ]),
        () => alive.current && id === suggestRequest.current,
      )
    } catch (error) {
      if (!alive.current || id !== suggestRequest.current) {
        return
      }
      setSuggestFailure(socialUniverseSuggestFailureKind(error))
    } finally {
      if (alive.current && id === suggestRequest.current) {
        setIsSuggesting(false)
      }
    }
  }

  /** The client's pick of the subject kind (D24); Suggest never overwrites it. */
  function pickSubjectKind(subjectKind: SocialSubjectKind) {
    subjectKindPicked.current = true
    updateField('subjectKind', subjectKind)
  }

  /** Adds one suggested keyword chip to the Topic's keywords. */
  function addSuggestedKeyword(keyword: string) {
    setSuggestedKeywords((current) => current.filter((entry) => entry !== keyword))
    if (!form.keywords.includes(keyword) && form.keywords.length < SOCIAL_MAX_KEYWORDS) {
      updateField('keywords', [...form.keywords, keyword])
    }
  }

  /** Adds one suggested brand chip to the Topic's brands. */
  function addSuggestedBrand(brand: SocialBrandInput) {
    setSuggestedBrands((current) => current.filter((entry) => entry.name !== brand.name))
    if (
      form.brands.length < SOCIAL_MAX_BRANDS &&
      newSuggestedNames(form.brands.map((entry) => entry.name), [brand.name]).length
    ) {
      updateField('brands', [...form.brands, brand])
    }
  }

  /** Adds one suggested theme as a new row. */
  function addSuggestedTheme(theme: SocialTopicInput) {
    setSuggestedThemes((current) => current.filter((entry) => entry.name !== theme.name))
    if (
      form.topics.length < SOCIAL_MAX_TOPICS &&
      newSuggestedNames(form.topics.map((topic) => topic.name), [theme.name]).length
    ) {
      updateField('topics', [...form.topics, theme])
    }
  }

  function togglePlatform(platform: SocialPlatform, isChecked: boolean) {
    if (isChecked) {
      updateField('platforms', [...new Set([...form.platforms, platform])])
      return
    }
    const isExisting = universe?.channels.some(
      (channel) => channel.platform === platform,
    )
    if (isExisting) {
      setPendingRemoval(platform)
      return
    }
    updateField(
      'platforms',
      form.platforms.filter((entry) => entry !== platform),
    )
  }

  function confirmRemoval() {
    if (pendingRemoval) {
      updateField(
        'platforms',
        form.platforms.filter((entry) => entry !== pendingRemoval),
      )
    }
    setPendingRemoval(null)
  }

  function cancelRemoval() {
    setPendingRemoval(null)
  }

  function addLanguage(code: string) {
    if (!code || form.languages.includes(code)) {
      return
    }
    if (form.languages.length >= SOCIAL_MAX_LANGUAGES) {
      return
    }
    updateField('languages', [...form.languages, code])
  }

  function removeLanguage(code: string) {
    updateField(
      'languages',
      form.languages.filter((entry) => entry !== code),
    )
  }

  function flushDrafts(): UniverseFormState {
    const next = flushUniverseDrafts(form, drafts)
    latestForm.current = next
    setForm(next)
    setDrafts(emptyUniverseDrafts())
    return next
  }

  /** Returns the first step that stops the save, or null once it ran. */
  async function save(): Promise<UniverseStep | null> {
    if (saving.current) {
      return null
    }
    const next = flushDrafts()
    setHasTriedSubmit(true)
    const blocked = firstBlockedUniverseStep(next)
    if (blocked) {
      return blocked
    }
    saving.current = true
    setIsSaving(true)
    setError(null)
    setIsOutOfCredits(false)
    try {
      const saved = universe
        ? await api.updateUniverse(universe.token, toUniverseUpdate(next))
        : await api.createUniverse(toUniverseInput(next))
      if (!alive.current) {
        return null
      }
      onSaved(saved)
      // The open drawer reads its own key: give it the saved Topic, then refetch.
      void mutateCache([SOCIAL_UNIVERSE_SWR_KEY, saved.token], saved, {
        revalidate: true,
      })
      void refreshSocialPostsUsage()
      toast.success(
        t(
          universe
            ? 'socialListening.topicToast.updated'
            : 'socialListening.topicToast.created',
        ),
      )
      onOpenChange(false)
    } catch (caught) {
      if (!alive.current) {
        return null
      }
      const isNoFunds = socialLimitErrorBody(caught)?.code === 'social_out_of_credits'
      setIsOutOfCredits(isNoFunds)
      // A refused search names its channel and the search: show that reason.
      const reason = (caught as { response?: { data?: { message?: unknown } } })?.response
        ?.data?.message
      setError(
        isNoFunds
          ? t('socialListening.credits.confirm.insufficient')
          : typeof reason === 'string' && reason
            ? reason
            : t('socialListening.topicDialog.saveFailed'),
      )
    } finally {
      if (alive.current) {
        saving.current = false
        setIsSaving(false)
      }
    }
    return null
  }

  return {
    form,
    drafts,
    isEditing: Boolean(universe),
    isSaving,
    isSuggesting,
    suggestFailed: suggestFailure !== null,
    suggestFailure,
    // No more chips once the Topic holds SOCIAL_MAX_KEYWORDS.
    suggestedKeywords:
      form.keywords.length < SOCIAL_MAX_KEYWORDS
        ? suggestedKeywords.filter((keyword) => !form.keywords.includes(keyword))
        : [],
    // Only warnings about keywords still on screen (in the Topic or offered).
    keywordWarnings: keywordWarnings.filter(
      (warning) =>
        form.keywords.includes(warning.keyword) || suggestedKeywords.includes(warning.keyword),
    ),
    suggestedBrands:
      form.brands.length < SOCIAL_MAX_BRANDS
        ? suggestedBrands.filter(
            (brand) =>
              newSuggestedNames(form.brands.map((entry) => entry.name), [brand.name]).length,
          )
        : [],
    suggestedThemes:
      form.topics.length < SOCIAL_MAX_TOPICS
        ? suggestedThemes.filter(
            (theme) =>
              newSuggestedNames(form.topics.map((topic) => topic.name), [theme.name]).length,
          )
        : [],
    refusedBrands,
    refusedSources,
    /** A source's check running, or why it gave no answer; null when it answered or has not run. */
    sourceCheckState: (
      kind: SocialSourceCheckKind,
      platform: SocialPlatform,
      value: string,
      brandName: string | null,
    ): SocialSourceCheckState | null => checkStates[sourceKey(kind, platform, value, brandName)] ?? null,
    pendingRemoval,
    error,
    isOutOfCredits,
    /** The Topic's runs (edit only), for the observed credits figure. */
    runs,
    missing: hasTriedSubmit ? missingUniverseFields(form) : [],
    removedPlatforms: removedPlatforms(universe, form.platforms),
    changesTags: changesTags(universe, form),
    updateField,
    updateMarket,
    updateDraft,
    updateHandleDraft,
    updateHandles,
    updateSearchDraft,
    updateSearches,
    updateHashtagDraft,
    updateHashtags,
    updateCommunityDraft,
    updateCommunities,
    setBrandAccount,
    addTypedBrandAccount: (brandIndex: number, platform: SocialPlatform, handle: string) =>
      void addTypedBrandAccount(brandIndex, platform, handle),
    pickSubjectKind,
    addSuggestedKeyword,
    addSuggestedBrand,
    addSuggestedTheme,
    togglePlatform,
    confirmRemoval,
    cancelRemoval,
    addLanguage,
    removeLanguage,
    suggest: () => void suggest(),
    flushDrafts,
    save,
  }
}

export type SocialUniverseWizard = ReturnType<typeof useSocialUniverseWizard>
